import React, { useState, useEffect } from 'react';
import { PencilSimple, Plus, Trash, X } from '@phosphor-icons/react';
import { editorAPI } from '../../utils/api';
import { toast } from 'sonner';

export const OrgEditorsSection = ({ organization }) => {
  const [editors, setEditors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { if (organization?.id) loadEditors(); }, [organization?.id]);

  const loadEditors = async () => {
    try {
      const { data } = await editorAPI.list(organization.id);
      setEditors(data.editors || []);
    } catch { setEditors([]); }
  };

  const handleAdd = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      const { data } = await editorAPI.add(organization.id, email.trim());
      toast.success(data.message);
      setEmail('');
      setShowAdd(false);
      loadEditors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add editor'); }
    finally { setAdding(false); }
  };

  const handleRemove = async (editorId, name) => {
    try {
      await editorAPI.remove(organization.id, editorId);
      toast.success(`${name} removed as editor`);
      loadEditors();
    } catch { toast.error('Failed to remove editor'); }
  };

  return (
    <div className="mb-8 bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="org-editors-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PencilSimple size={18} className="text-[#FF9500]" />
          <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Show Editors</h3>
          <span className="text-[#525252] text-xs">({editors.length})</span>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded-md text-sm font-semibold hover:bg-[#FF9500]/25 transition-colors" data-testid="add-editor-btn">
            <Plus size={14} weight="bold" /> Add Editor
          </button>
        )}
      </div>

      <p className="text-[#9E9E9E] text-xs mb-4">Editors can create, edit, and manage shows for your organization.</p>

      {showAdd && (
        <div className="flex gap-2 mb-4" data-testid="add-editor-form">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter editor's email address"
            className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] placeholder-[#525252]"
            data-testid="editor-email-input"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            autoFocus
          />
          <button onClick={handleAdd} disabled={adding} className="px-4 py-2 bg-[#FF9500] text-white text-sm font-bold rounded-md hover:bg-[#FF9500]/80 disabled:opacity-50" data-testid="editor-add-confirm">
            {adding ? '...' : 'Add'}
          </button>
          <button onClick={() => { setShowAdd(false); setEmail(''); }} className="p-2 text-[#9E9E9E] hover:text-white">
            <X size={16} />
          </button>
        </div>
      )}

      {editors.length === 0 ? (
        <p className="text-[#525252] text-sm">No editors added yet. Only you can manage shows.</p>
      ) : (
        <div className="space-y-2">
          {editors.map(e => (
            <div key={e.id} className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3" data-testid={`editor-${e.id}`}>
              <div>
                <p className="text-white text-sm font-semibold">{e.name}</p>
                <p className="text-[#9E9E9E] text-xs">{e.email}</p>
              </div>
              <button onClick={() => handleRemove(e.id, e.name)} className="text-[#FF3B30] hover:text-[#FF3B30]/80 transition-colors" data-testid={`remove-editor-${e.id}`} title="Remove editor">
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
